import React, { useState } from 'react';
import { Radio, X, MapPin } from 'lucide-react';

// Rep-only control: a floating status pill (top-center) that opens a modal to
// set a display name and toggle live location broadcasting to all users.
const RepBroadcastPanel = ({
  isBroadcasting, setIsBroadcasting,
  broadcastName, setBroadcastName,
  hasLocation,
}) => {
  const [open, setOpen] = useState(false);
  const canGoLive = hasLocation && broadcastName.trim().length > 0;

  return (
    <>
      {/* Floating status pill.
          Wrapped in a safe-area-aware container: on native the WebView runs full
          screen *under* the status bar, so a bare `top-2` put this pill behind the
          notch / Dynamic Island (invisible and untappable). `pt-safe` uses
          env(safe-area-inset-top) — viewport-fit=cover is set in index.html — the
          same way the HUD and RecapCard already do it. */}
      <div className="fixed top-0 left-0 right-0 z-[1500] flex justify-center pt-safe pointer-events-none">
        <button
          onClick={() => setOpen(true)}
          className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-full border-2 font-black uppercase tracking-widest text-[10px] shadow-lg transition-colors pointer-events-auto ${
            isBroadcasting
              ? 'bg-brand-pink text-black border-white animate-pulse'
              : 'bg-black/90 text-white border-zinc-600'
          }`}
        >
          <Radio size={13} />
          {isBroadcasting ? 'LIVE · Broadcasting' : 'Rep · Off-air'}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[9500] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-3xl p-6 shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <Radio className="text-brand-pink" size={18} />
              <h2 className="text-lg font-black uppercase tracking-tight text-white">Rep Broadcast</h2>
            </div>
            <p className="text-[11px] text-zinc-400 font-bold mb-5 leading-relaxed">
              Go live to share your location with{' '}
              <span className="text-brand-teal">every user</span> so they can find you for
              product sales &amp; activations.
            </p>

            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1 block">
              Display Name
            </label>
            <input
              type="text"
              value={broadcastName}
              onChange={(e) => setBroadcastName(e.target.value)}
              placeholder="e.g. Cody – Bender Rep"
              maxLength={40}
              className="w-full bg-black border border-zinc-700 rounded-xl p-3 text-sm font-bold text-white mb-4 focus:outline-none focus:border-brand-pink"
            />

            {!hasLocation && (
              <div className="flex items-center gap-2 text-[11px] font-bold text-amber-400 mb-4">
                <MapPin size={14} /> Waiting for GPS lock… allow location to broadcast.
              </div>
            )}

            <button
              onClick={() => setIsBroadcasting(!isBroadcasting)}
              disabled={!isBroadcasting && !canGoLive}
              className={`w-full py-4 rounded-xl font-black uppercase tracking-widest text-sm transition-transform active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed ${
                isBroadcasting ? 'bg-zinc-700 text-white' : 'bg-brand-pink text-black'
              }`}
            >
              {isBroadcasting ? 'Stop Broadcasting' : 'Go Live'}
            </button>

            {isBroadcasting && (
              <p className="text-center text-[10px] font-black text-brand-pink uppercase tracking-widest mt-3 animate-pulse">
                ● You are live on the map
              </p>
            )}

            <p className="text-center text-[9px] font-bold text-zinc-500 mt-3 leading-relaxed">
              Keeps the screen on while live so your location updates in your pocket.
              Switching to another app or locking the phone pauses the broadcast.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default RepBroadcastPanel;
